import "./_review.scss";
import React, { useContext, useEffect, useState, useRef } from "react";
// redux
import { useSelector } from "react-redux";
import { callCount as callCountData } from "@src/@slice/globalSlice";
import { selectWindowDataList } from "@src/@slice/interWindowSlice";
// review
import { ICommunityListItem } from "@src/api/community/community";
import { ReviewProviderData } from "./Provider";
import { useReviewActions } from "./useReviewActions";
// components
import { ReviewItem, BlindReviewItem } from "./Item";
import InfiniteScroll from "react-infinite-scroll-component";
import NoData from "@src/components/common/noData/NoData";
import LoadingSpinner from "@src/components/common/loading/LoadingSpinner";
import Blank from "@src/components/common/blank/Blank";

// 커뮤니티 리뷰 레이아웃
export const ReviewCommunityWrap = ({
  activeParentTabId,
  initialScrollTop,
  showLoader,
}: {
  activeParentTabId?: number;
  initialScrollTop?: number;
  showLoader: (state: boolean) => void;
}) => {
  // context
  const { parentTabNumber, showAllReview, communityList, filter, activeReviewTab, refreshFlag } =
    useContext(ReviewProviderData).data;

  // actions
  const { getBlindReviewList, reviewListCall, REVIEW_IS_FETCHING, REVIEW_IS_FETCH_END } =
    useReviewActions();

  // call count
  const callCount = useSelector(callCountData);

  // window data list
  const ForWindowDataList = useSelector(selectWindowDataList);

  // reviewList 영역 참조
  const reviewListRef = useRef<HTMLDivElement>(null);

  // provider data 참조
  const providerDataRef = useRef({ showAllReview, callCount, activeReviewTab, filter });

  // 호출 카운트 참조
  const countRef = useRef(0);

  // 모달 내 렌더링 여부
  const modalCheck = useRef<boolean>(false);

  // 리뷰 리스트 호출
  useEffect(() => {
    providerDataRef.current = {
      ...providerDataRef.current,
      showAllReview,
      activeReviewTab,
      filter,
    };
    if (
      // 부모 탭 넘버가 있을 경우 현재 활성 탭 넘버와 같을 경우 && 리뷰 로딩 중이 아니고 && 현재 활성 탭이 0일 경우에만 호출
      (parentTabNumber === activeParentTabId || parentTabNumber === null) &&
      !REVIEW_IS_FETCHING &&
      providerDataRef.current.activeReviewTab === 0
    ) {
      getBlindReviewList();
      reviewListCall("community", "init");
    }
  }, [showAllReview, filter, refreshFlag]);

  // callCount 변경
  useEffect(() => {
    providerDataRef.current = { ...providerDataRef.current, callCount };
    if (callCount === 0) return;
    // 스킴 통해 다른 페이지 열고 닫거나 하는 경우에 값이 올라가는데 리뷰상세나 이미지를 본 경우, 신고 모달 상세를 연 경우에는 초기화하지 않도록 처리
    // 리뷰쓰기 등 다른 페이지로 이동하고 돌아온 경우에는 새로고침을 위해 초기화
    if (
      !ForWindowDataList["reviewDetailSection"] &&
      !ForWindowDataList["image"] &&
      !ForWindowDataList["report"]
    ) {
      getBlindReviewList();
      reviewListCall("community", "init");
    }
  }, [callCount]);

  // 모달 내 렌더링 여부 체크
  useEffect(() => {
    if (reviewListRef?.current) {
      modalCheck.current = !!reviewListRef.current?.closest(".full-height-wrap");
    }
  }, [reviewListRef?.current]);

  // 스크롤 위치 초기화 및 스크롤 이동
  useEffect(() => {
    if (communityList === null) return;
    countRef.current++;
    if (countRef.current === 0) return;
    if (modalCheck.current) {
      setTimeout(() => {
        reviewListRef.current?.closest("[class*='wrap_contents-wrap__']")?.scrollTo({
          top: 0,
          behavior: "smooth",
        });
      }, 100);
    } else {
      if (initialScrollTop === 0) return;
      setTimeout(() => {
        window.scrollTo({ top: initialScrollTop, behavior: "smooth" });
      }, 100);
    }
  }, [showAllReview, activeReviewTab, filter]);

  // loader 노출 여부
  useEffect(() => {
    showLoader(REVIEW_IS_FETCHING);
  }, [REVIEW_IS_FETCHING]);

  return (
    <>
      <div
        className="review-list"
        style={{ display: `${activeReviewTab === 0 ? "" : "none"}` }}
        ref={reviewListRef}
      >
        {communityList !== null && communityList.length > 0 && (
          <InfiniteScroll
            dataLength={communityList.length}
            next={() => {
              reviewListCall("community", "add");
            }}
            hasMore={
              !REVIEW_IS_FETCHING && activeReviewTab === 0 && !REVIEW_IS_FETCH_END ? true : false
            }
            loader={null}
            scrollThreshold={0.7}
            style={{ overflow: "visible" }}
          >
            {communityList.map((list: ICommunityListItem, cIdx: number) => (
              <div
                className={`review-item border-top-gray100`}
                key={`review-item-${list.id}-${cIdx}`}
              >
                <ReviewItem list={list} reviewType="community" />
              </div>
            ))}
          </InfiniteScroll>
        )}
        {communityList !== null && communityList.length === 0 && (
          <>
            <Blank height={40} />
            <NoData title={"아직 작성된 리뷰가 없습니다."} desc={"리뷰를 작성해보세요."} />
          </>
        )}
      </div>
    </>
  );
};

// 블로그 리뷰 레이아웃
export const BlogReviewCommunityWrap = ({
  activeParentTabId,
  initialScrollTop,
  showLoader,
}: {
  activeParentTabId?: number;
  initialScrollTop?: number;
  showLoader: (state: boolean) => void;
}) => {
  // context
  const { parentTabNumber, blogReviewList, activeReviewTab, refreshFlag } =
    useContext(ReviewProviderData).data;

  // actions
  const { reviewListCall, REVIEW_IS_FETCHING, REVIEW_IS_FETCH_END } = useReviewActions();

  // call count
  const callCount = useSelector(callCountData);

  // reviewList 영역 참조
  const reviewListRef = useRef<HTMLDivElement>(null);

  // provider data 참조
  const providerDataRef = useRef({ callCount, activeReviewTab });

  // 호출 카운트 참조
  const countRef = useRef(0);

  // 모달 내 렌더링 여부
  const modalCheck = useRef<boolean>(false);

  // 모달 내 렌더링 여부 체크
  useEffect(() => {
    if (reviewListRef?.current) {
      modalCheck.current = !!reviewListRef.current?.closest(".full-height-wrap");
    }
  }, [reviewListRef?.current]);

  // 리뷰 리스트 호출
  useEffect(() => {
    providerDataRef.current = { callCount, activeReviewTab };
    if (
      // 부모 탭 넘버가 있을 경우 현재 활성 탭 넘버와 같을 경우 && 리뷰 로딩 중이 아니고 && 현재 활성 탭이 1일 경우에만 호출
      (parentTabNumber === activeParentTabId || parentTabNumber === null) &&
      !REVIEW_IS_FETCHING &&
      providerDataRef.current.activeReviewTab === 1
    ) {
      reviewListCall("blog", "init");
    }
  }, [activeReviewTab, refreshFlag, callCount]);

  // 스크롤 위치 초기화 및 스크롤 이동
  useEffect(() => {
    if (blogReviewList === null) return;
    countRef.current++;
    if (countRef.current === 0) return;
    if (modalCheck.current) {
      setTimeout(() => {
        reviewListRef.current?.closest("[class*='wrap_contents-wrap__']")?.scrollTo({
          top: 0,
          behavior: "smooth",
        });
      }, 100);
    } else {
      if (initialScrollTop === 0) return;
      setTimeout(() => {
        window.scrollTo({ top: initialScrollTop, behavior: "smooth" });
      }, 100);
    }
  }, [activeReviewTab]);

  // loader 노출 여부
  useEffect(() => {
    showLoader(REVIEW_IS_FETCHING);
  }, [REVIEW_IS_FETCHING]);

  return (
    <>
      <div
        className="review-list"
        style={{ display: `${activeReviewTab === 1 ? "" : "none"}` }}
        ref={reviewListRef}
      >
        {blogReviewList !== null && blogReviewList.length > 0 && (
          <InfiniteScroll
            dataLength={blogReviewList.length}
            next={() => reviewListCall("blog", "add")}
            hasMore={
              activeReviewTab === 1 && !REVIEW_IS_FETCH_END && !REVIEW_IS_FETCHING ? true : false
            }
            loader={null}
            style={{ overflow: "visible" }}
            scrollThreshold={0.7}
          >
            {blogReviewList.map((list: any, cIdx: number) => (
              <div
                className={`review-item review-item-blog border-top-gray100`}
                key={`review-item-${list.uuid}-${cIdx}`}
              >
                <ReviewItem list={list} reviewType="blog" />
              </div>
            ))}
          </InfiniteScroll>
        )}
        {blogReviewList !== null && blogReviewList.length === 0 && (
          <>
            <Blank height={40} />
            <NoData title={"아직 작성된 리뷰가 없습니다."} desc={"리뷰를 작성해보세요."} />
          </>
        )}
      </div>
    </>
  );
};

// 블라인드 리뷰 레이아웃
export const BlindReviewCommunityWrap = ({ partnerUuid }: { partnerUuid: string }) => {
  // context
  const { blindList } = useContext(ReviewProviderData).data;

  // actions
  const { getBlindReviewList } = useReviewActions();

  // loader 노출 여부
  const [showLoader, setShowLoader] = useState<boolean>(true);

  // 블라인드 리뷰 리스트 호출
  useEffect(() => {
    getBlindReviewList({ partnerUuid })
      .then(() => {})
      .catch(() => {
        alert(`블라인드된 리뷰를 불러오는 중 오류가 발생했습니다. 잠시 후 다시 시도해주세요.`);
      })
      .finally(() => {
        setShowLoader(false);
      });
  }, []);

  return (
    <>
      <div className="review-list">
        {blindList !== null &&
          blindList.length > 0 &&
          blindList.map((list: ICommunityListItem, cIdx: number) => (
            <div
              className={`review-item border-top-gray100`}
              key={`review-item-${list.id}-${cIdx}`}
            >
              <BlindReviewItem list={list} reviewType="blind" viewType="list-summary" />
            </div>
          ))}
        {blindList !== null && blindList.length === 0 && (
          <>
            <Blank height={40} />
            <NoData title={"블라인드된 리뷰가 없습니다."} desc={""} />
          </>
        )}
      </div>
      {showLoader && <LoadingSpinner />}
    </>
  );
};
